import { useState } from "react";
import { Button, Card, Table, EmptyState, Badge, useToast, type Column } from "@/ui";
import { useT } from "@/app";
import { useCan } from "@/auth";
import { token } from "@/design/theme";
import { ForbiddenInline } from "./ForbiddenInline";
import { Mono } from "./Mono";
import { useEnqueueDemande, usePlanTubes } from "../lib/api";
import { CAPS } from "../lib/caps";
import type { Demande, PlannedTube } from "../lib/types";

/**
 * TubesPanel — tube generation + the prélèvement handoff (§6). Generate mints each tube's matricule +
 * opaque QR token (raw returned ONCE → print vignettes immediately, R-08); enqueue hands ONE ticket
 * per patient to the sampling queue (R-05). Generation requires the demande to be `ouverte` (i.e.
 * facturée) — the server enforces this; we surface the hint when it isn't.
 */
export function TubesPanel({ demande }: { demande: Demande }) {
  const { t } = useT();
  const toast = useToast();
  const canGenerate = useCan(CAPS.tubeGenerate);

  const plan = usePlanTubes(demande.id);
  const enqueue = useEnqueueDemande(demande.id);
  const [tubes, setTubes] = useState<PlannedTube[]>([]);

  const canGenNow = demande.statut === "ouverte" || demande.statut === "tubes_generes";
  const alreadyGenerated = demande.statut === "tubes_generes";

  if (!canGenerate) {
    return <ForbiddenInline requiredCapabilities={[CAPS.tubeGenerate]} />;
  }

  const generate = () => {
    plan.mutate(
      {},
      {
        onSuccess: (res) => {
          setTubes(res.tubes);
          toast.success(t("reception.tubes.generated", { n: res.tubes.length }));
        },
        onError: (e) => toast.error(e.message || t("reception.error.generic")),
      },
    );
  };

  const send = () => {
    enqueue.mutate(undefined, {
      onSuccess: (res) =>
        toast.success(t("reception.tubes.enqueued", { n: String(res.ticket?.numero ?? res.ticket?.id ?? "") })),
      onError: (e) => toast.error(e.message || t("reception.error.generic")),
    });
  };

  const print = () => window.print();

  const columns: ReadonlyArray<Column<PlannedTube>> = [
    {
      key: "matricule",
      header: t("reception.tubes.col.matricule"),
      cell: (tube) => <Mono>{tube.matricule}</Mono>,
      mono: true,
    },
    {
      key: "type",
      header: t("reception.tubes.col.type"),
      cell: (tube) => <Mono>{tube.type_tube_code}</Mono>,
      mono: true,
    },
    {
      key: "disciplines",
      header: t("reception.tubes.col.disciplines"),
      cell: (tube) => (
        <span style={{ display: "flex", gap: token.s1, flexWrap: "wrap" }}>
          {tube.discipline_codes.map((d) => (
            <Badge key={d} tone="info">
              {d}
            </Badge>
          ))}
        </span>
      ),
    },
  ];

  return (
    <Card
      title={t("reception.tubes.title")}
      actions={
        <div style={{ display: "flex", gap: token.s2 }}>
          <Button onClick={generate} loading={plan.isPending} disabled={!canGenNow}>
            {t("reception.tubes.generate")}
          </Button>
        </div>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: token.s4 }}>
        {!canGenNow && (
          <p role="status" style={{ margin: 0, color: token.warn }}>
            ▲ {t("reception.tubes.needOpen")}
          </p>
        )}

        {tubes.length === 0 && !alreadyGenerated ? (
          <EmptyState
            glyph="▥"
            title={t("reception.tubes.empty")}
            description={t("reception.tubes.emptyDesc")}
          />
        ) : (
          <Table<PlannedTube>
            columns={columns}
            rows={tubes}
            rowKey={(tubeRow) => String(tubeRow.id)}
            caption={t("reception.tubes.title")}
            emptyTitle={t("reception.tubes.empty")}
            emptyDescription={alreadyGenerated ? t("reception.tubes.print") : t("reception.tubes.emptyDesc")}
          />
        )}

        <div style={{ display: "flex", gap: token.s2, justifyContent: "flex-end", flexWrap: "wrap" }}>
          {tubes.length > 0 && (
            <Button variant="secondary" onClick={print}>
              {t("reception.tubes.print")}
            </Button>
          )}
          <Button
            onClick={send}
            loading={enqueue.isPending}
            disabled={!alreadyGenerated && tubes.length === 0}
          >
            {t("reception.tubes.enqueue")}
          </Button>
        </div>
      </div>
    </Card>
  );
}
